import { Component, ElementRef, forwardRef, HostBinding, Injector, Input, model, OnInit, ViewChild } from '@angular/core';
import { provideNativeDateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import moment, { Moment } from 'moment';
import { DateRangeAddIdDirective } from 'app/shared/directives/date-range-add-id.directive';

@Component({
  selector: 'app-horus-date-range',
  standalone: true,
  imports: [
    MatSelectModule,
    MatListModule,
    MatExpansionModule,
    MatDatepickerModule,
    DateRangeAddIdDirective
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MatFormFieldControl, useExisting: HorusDateRangeComponent },
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => HorusDateRangeComponent),
    },
    { provide: MAT_DATE_LOCALE, useValue: 'es-ES' },
  ],
  templateUrl: './horus-date-range.component.html',
  styleUrl: './horus-date-range.component.scss'
})
export class HorusDateRangeComponent implements 
  OnInit,
  MatFormFieldControl<string>,
  ControlValueAccessor {

  private readonly dateRangeTypeMap: { [key: string]: () => any } = {
    'yesterday': () => this.getDateRangeYesterday(),
    'lastWeek': () => this.getDateRangeLastWeek(),
    'lastMonth': () => this.getDateRangeLastMonth(),
    'last3Month': () => this.getDateRangeLast3Month(),
    'lastYear': () => this.getDateRangeLastYear(),
  };

  selected = model<Moment | null>(null);

  @ViewChild(MatSelect) matSelect: MatSelect;
  @Input()
  touched = false;

  onChange: any = () => { };

  onTouched: any = () => { };

  value: string;
  readonly stateChanges: Subject<void> = new Subject<void>();
  id: string;
  placeholder: string;
  ngControl: NgControl;
  focused: boolean = false;
  empty: boolean;
  required: boolean;
  disabled: boolean;
  errorState: boolean;
  controlType?: string | undefined;
  autofilled?: boolean | undefined;
  userAriaDescribedBy?: string | undefined;

  dateRangeOptions: any[] = [];
  selectCustomMonth: any[] = [];
  currentYear: number = 2025;
  showCustomMonth: boolean = false;
  selectedRangeType: string | null = null;
  
  focusMonitorSubscription: Subscription;

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || (this.value!=null && this.value!=undefined && this.value!='');
  }

  constructor(
    public injector: Injector,
    private focusMonitor: FocusMonitor,
    private elRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    this.dateRangeOptions = [
      { id: 'global-date-range-btn-yesterday', type: 'yesterday', description: 'Últimas 24 horas', function: () => this.setValueControl('yesterday')},
      { id: 'global-date-range-btn-last-week', type: 'lastWeek', description: 'Última semana', function: () => this.setValueControl('lastWeek')},
      { id: 'global-date-range-btn-last-month', type: 'lastMonth', description: 'Últimos 30 días', function: () => this.setValueControl('lastMonth')},
      { id: 'global-date-range-btn-last-3-month', type: 'last3Month', description: 'Últimos 90 días', function: () => this.setValueControl('last3Month')},
      { id: 'global-date-range-btn-personalized-month', type: 'customMonth', description: 'Mes personalizado', function: () => this.openSectionCustomMonth()},
      //{ description: 'Último año', function: () => this.setValueControl('lastYear')},
    ];
    this.focusMonitorSubscription = this.focusMonitor
    .monitor(this.elRef.nativeElement, true)
    .subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  setValueControl(typeData: string, param?:any) {
    let mapFunction: { [key: string]: () => any } = {
      ...this.dateRangeTypeMap,
      'customMonth': ()=>this.getDateRangeCustomMonth(param!),
    };
    let result = mapFunction[typeData]?mapFunction[typeData]():'';
    if(result!=''){
      result = result.from + ' - '+ result.to;
    }
    this.value = result;
    this.selectedRangeType = typeData;
    this.onChange(this.value);
    this.stateChanges.next();
    this.matSelect.close();
  }

  isOptionSelected(typeData: string): boolean {
    if (this.selectedRangeType) {
      return this.selectedRangeType === typeData;
    }

    return this.getValueByType(typeData) === this.value;
  }

  private getValueByType(typeData: string): string {
    let result = this.dateRangeTypeMap[typeData]?.();
    return result ? result.from + ' - ' + result.to : '';
  }

  getDateRangeYesterday(){
    return {
      from: moment().subtract(1, 'day').format('DD/MM/YYYY'),//format('YYYY-MM-DDT00:00:00.000Z'),
      to: moment().format('DD/MM/YYYY'),//format('YYYY-MM-DDT23:59:59.999Z')
    };
  }

  getDateRangeLastWeek(){
    return {
      from: moment().subtract(1, 'week').format('DD/MM/YYYY'),//format('YYYY-MM-DDT00:00:00.000Z'),
      to: moment().format('DD/MM/YYYY'),//format('YYYY-MM-DDT23:59:59.999Z')
    };
  }

  getDateRangeLastMonth(){
    return {
      from: moment().subtract(1, 'month').format('DD/MM/YYYY'),//format('YYYY-MM-DDT00:00:00.000Z'),
      to: moment().format('DD/MM/YYYY'),//format('YYYY-MM-DDT23:59:59.999Z')
    };
  }

  getDateRangeLast3Month(){
    return {
      from: moment().subtract(3, 'month').format('DD/MM/YYYY'),//format('YYYY-MM-DDT00:00:00.000Z'),
      to: moment().format('DD/MM/YYYY'),//format('YYYY-MM-DDT23:59:59.999Z')
    };
  }

  getDateRangeLastYear(){
    return {
      from: moment().subtract(1, 'year').format('DD/MM/YYYY'),//format('YYYY-MM-DDT00:00:00.000Z'),
      to: moment().format('DD/MM/YYYY'),//format('YYYY-MM-DDT23:59:59.999Z')
    };
  }

  getDateRangeCustomMonth({year,month}: any) {
    let monthMoment = moment().year(year).month(month);
    return {
      from: monthMoment.format('01/MM/YYYY'),//format('YYYY-MM-01T00:00:00.000Z'),
      to: monthMoment.endOf('month').format('DD/MM/YYYY'),//format('YYYY-MM-DDT23:59:59.999Z')
    }
  }

  setMonthAndYear(datePickerSelected: Date) {
    this.setValueControl('customMonth', { year:datePickerSelected.getFullYear(), month: datePickerSelected.getMonth() })
    this.showCustomMonth = false;
  }

  openSectionCustomMonth() {
    this.showCustomMonth = !this.showCustomMonth;
  }

  setDescribedByIds(ids: string[]): void {}
  onContainerClick(event: MouseEvent): void {}

  updateErrorState(): void {}
    
  writeValue(obj: any): void {
    if (obj == null) {
      this.touched = false;
      this.selectedRangeType = null;
    }
    if (obj !== undefined) {
      this.value = obj;
      this.selectedRangeType = this.dateRangeOptions.find((option) => this.getValueByType(option.type) === obj)?.type ?? null;
      this.stateChanges.next();
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  handleSelection(selection: any) {
    this.touched = true;
  }
}
